import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import SectionHeader from "./SectionHeader";
import StatCard from "./StatCard";
import { carrierBadgeClass } from "../presentation";

function CarrierBreakdown({ carrier, data }) {
  const rate = data.total > 0 ? (data.hit / data.total) * 100 : 0;
  const barColor =
    rate >= 90 ? "bg-emerald-500" : rate >= 75 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="rounded-2xl border border-slate-200 px-4 py-3.5 hover:border-slate-300 transition-colors">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${carrierBadgeClass(
              carrier
            )}`}
          >
            {carrier}
          </span>
          <p className="text-sm text-slate-600">{data.total} records</p>
        </div>
        <p className="text-sm font-bold text-slate-700">{rate.toFixed(1)}%</p>
      </div>
      <div className="mb-2.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${rate}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold">
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-emerald-700 ring-1 ring-emerald-200">
          HIT {data.hit}
        </span>
        <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-red-700 ring-1 ring-red-200">
          MISS {data.miss}
        </span>
      </div>
    </div>
  );
}

export default function AutomationSummary({
  summary,
  difotPct,
  difotColors,
  processedRecordCount,
  hasProcessedFiles,
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={faChartBar}
        iconBg="bg-[#144677]/10"
        iconColor="text-[#144677]"
        title="Automation Summary"
        subtitle="Carrier data takes priority over Machship data."
        badge={
          processedRecordCount > 0 ? (
            <span className="flex-shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-600">
              {processedRecordCount} records
            </span>
          ) : null
        }
      />

      <div className={`mb-4 rounded-2xl border p-5 ${difotColors.bg} ${difotColors.border}`}>
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Overall DIFOT Rate
            </p>
            <p className={`mt-1 text-6xl font-black leading-none ${difotColors.text}`}>
              {difotPct}
            </p>
            <p className="mt-1.5 text-xs text-slate-400">delivery in full on time</p>
          </div>
          {summary.total > 0 && (
            <div className="text-right">
              <p className="text-xs text-slate-400">based on</p>
              <p className="text-2xl font-black text-slate-700">{summary.total}</p>
              <p className="text-xs text-slate-400">records</p>
            </div>
          )}
        </div>
        {summary.total > 0 && (
          <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-white/60">
            <div
              className={`h-full rounded-full transition-all duration-700 ${difotColors.bar}`}
              style={{ width: difotPct === "—" ? "0%" : difotPct }}
            />
          </div>
        )}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <StatCard label="Hit" value={summary.hit} tone="success" />
        <StatCard label="Miss" value={summary.miss} tone="danger" />
      </div>

      <div className="space-y-2.5">
        {Object.entries(summary.byCarrier).map(([carrier, data]) => (
          <CarrierBreakdown key={carrier} carrier={carrier} data={data} />
        ))}

        {!hasProcessedFiles && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <FontAwesomeIcon icon={faChartBar} className="text-xl text-slate-400" />
            </div>
            <p className="text-sm text-slate-500">
              Upload a carrier file to populate the summary.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
